import _ from 'lodash';
import React from 'react';

import {default as Modal, withModal} from 'components/Modal';
import CreditNoteStatusTag from 'components/CreditNoteStatusTag';
import VisibleIf from 'components/VisibleIf';
import ExternalLink from 'components/ExternalLink';

import {
  BaseInformation,
  CreditNoteItemsTable,
  Totals,
  LessSection,
  RemainingCredit,
  ReceiveCashRefund
} from './components';

import {
  MODALS,
  SCHEDULE_ITEM_TYPES,
  fetchCreditNote,
  voidObject,
  addXeroId,
  syncWithXero,
  fetchXeroAccounts,
  ACCOUNT_TYPES,
  FETCH_SCHEDULE_DELAY,
  fetchInvoiceAllocatedCreditNotesXeroLinks,
  deallocateCreditNoteFromInvoice
} from 'ducks/tenancy';
import {delayedAction, composeActions} from 'ducks/common';
import {
  MODALS as BASE_MODALS,
  openModal,
  closeModal,
  storeDataInModal
} from 'ducks/modals';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import './styles.css';

class CreditNoteDetailModal extends React.Component<any> {
  componentDidMount() {
    const {data: {creditNoteId, locationRef}} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS, locationRef},
      () => {
        const fetchCreditNoteExtraActions = this.props.storeDataExtraActions(
          data => ({
            creditNote: data
          })
        );
        this.props.fetchCreditNote({creditNoteId}, fetchCreditNoteExtraActions);

        const fetchXeroAccountsExtraAction = this.props.storeDataExtraActions(
          data => ({
            xeroAccounts: data
          })
        );
        this.props.fetchXeroAccounts(
          {
            accountType: ACCOUNT_TYPES.BANK,
            location: locationRef
          },
          fetchXeroAccountsExtraAction
        );
      }
    );
  }

  componentDidUpdate(prevProps) {
    if (
      _.get(this.props, 'data.creditNote.invoices[0].id') !==
      _.get(prevProps, 'data.creditNote.invoices[0].id')
    ) {
      const invoiceId = _.get(this.props, 'data.creditNote.invoices[0].id');
      const {tenancyId} = this.props.data;

      const fetchInvoiceAllocatedCreditNotesXeroLinksAction = this.props.storeDataExtraActions(
        data => ({
          invoiceAllocationWarnings: data
        })
      );

      this.props.fetchInvoiceAllocatedCreditNotesXeroLinks(
        {invoiceId, tenancyId},
        fetchInvoiceAllocatedCreditNotesXeroLinksAction
      );
    }
  }

  openConfirmationModal = ({heading, confirmAction, question, warnings}) =>
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading,
      question,
      warnings,
      confirmAction
    });

  getSuccessExtraActions = () => {
    return [
      ...this.props.closeModalExtraActions,
      ...this.props.refetchExtraActions({
        tenancyId: this.props.data.tenancyId,
        scheduleType: this.props.data.scheduleType
      })
    ];
  };

  delayedFetchScheduleAction = () =>
    delayedAction({
      delay: FETCH_SCHEDULE_DELAY,
      action: () =>
        composeActions(
          this.props.delayExtraActions({
            tenancyId: this.props.data.tenancyId,
            scheduleType: this.props.data.scheduleType
          })
        )
    });

  voidCreditNoteConfirmAction = () => {
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ...this.getSuccessExtraActions(),
      this.delayedFetchScheduleAction
    ];
    const failureExtraAction = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.voidObject(
      {
        objectId: this.props.data.creditNoteId,
        objectType: SCHEDULE_ITEM_TYPES.CREDIT_NOTE,
        scheduleType: this.props.data.scheduleType,
        tenancyId: this.props.data.tenancyId
      },
      successExtraActions,
      failureExtraAction
    );
  };

  voidCreditNote = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VOID_SCHEDULE_ITEMS
      },
      () =>
        this.openConfirmationModal({
          heading: 'Void Credit Note',
          question: 'Are you sure you want to void this credit note?',
          confirmAction: this.voidCreditNoteConfirmAction,
          warnings: []
        })
    );

  deallocateCreditNoteFromInvoiceConfirmAction = () => {
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ...this.getSuccessExtraActions(),
      this.delayedFetchScheduleAction
    ];
    const failureExtraAction = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.deallocateCreditNoteFromInvoice(
      {
        invoiceId: _.get(this.props, 'data.creditNote.invoices[0].id'),
        tenancyId: this.props.data.tenancyId,
        creditNoteId: this.props.data.creditNoteId
      },
      successExtraActions,
      failureExtraAction
    );
  };

  deallocateCreditNoteFromInvoice = () => {
    const {warnings} = this.props.data;
    const modal_warnings = (
      <div>
        Due to limitations of the Xero API, you must manually deallocate any
        allocated credit note from the invoice in the Xero user interface and
        then deallocate it from Colab.
        <div>Please, follow the instructions: </div>
        <div>1) Click on every credit note detail link listed below. </div>
        <div>
          2) In Xero user interface click the `X` icon next to the invoice
          allocation line.
        </div>
        <div>3) Then come back to Colab and try to deallocate again.</div>
        <div>List of allocated credit notes in Xero:</div>
        <div>
          <ul>
            {!_.isEmpty(warnings) &&
              warnings.map(warning => (
                <li key={warning.xeroNumber}>
                  <div>
                    <ExternalLink
                      key={warning.xeroNumber}
                      href={warning.link}
                      targetBlank>
                      {warning.xeroNumber}
                    </ExternalLink>
                  </div>
                </li>
              ))}
          </ul>
        </div>
      </div>
    );

    return this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.DEALLOCATE_CREDIT_NOTE
      },
      () =>
        this.openConfirmationModal({
          heading: 'Deallocate credit note',
          warnings: !_.isEmpty(warnings) && [modal_warnings],
          question: 'Are you sure you want to deallocate the CreditNote?',
          confirmAction: this.deallocateCreditNoteFromInvoiceConfirmAction
        })
    );
  };

  addXeroIdToCreditNote = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.ADD_XERO_ID
      },
      () => {
        const xeroId = window.prompt('Xero ID: ', '');
        if (!_.isEmpty(xeroId)) {
          const successExtraActions = [
            ...this.getSuccessExtraActions(),
            this.delayedFetchScheduleAction
          ];
          const failureExtraActions = this.props.storeDataExtraActions(
            ({errors}) => ({errors})
          );

          this.props.addXeroId(
            {
              xeroId,
              objectId: this.props.data.creditNoteId,
              objectType: SCHEDULE_ITEM_TYPES.CREDIT_NOTE,
              scheduleType: this.props.data.scheduleType,
              tenancyId: this.props.data.tenancyId
            },
            successExtraActions,
            failureExtraActions
          );
        }
      }
    );

  syncCreditNoteWithXeroConfirmAction = () => {
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ...this.getSuccessExtraActions(),
      this.delayedFetchScheduleAction
    ];
    const failureExtraAction = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.syncWithXero(
      {
        objectId: this.props.data.creditNoteId,
        objectType: SCHEDULE_ITEM_TYPES.CREDIT_NOTE,
        scheduleType: this.props.data.scheduleType,
        tenancyId: this.props.data.tenancyId
      },
      successExtraActions,
      failureExtraAction
    );
  };

  syncCreditNoteWithXero = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.SYNC_WITH_XERO
      },
      () =>
        this.openConfirmationModal({
          heading: 'Sync Credit Note',
          question: 'Are you sure you want to sync this credit note with xero?',
          confirmAction: this.syncCreditNoteWithXeroConfirmAction,
          warnings: []
        })
    );

  openRefundDetailModal = refundId =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS
      },
      () => {
        const {scheduleType, locationRef, tenancyId} = this.props.data;

        this.props.composeActions([
          () => closeModal(MODALS.CREDIT_NOTE_DETAIL),
          () =>
            openModal(
              MODALS.REFUND_DETAIL,
              {
                scheduleType,
                refundId,
                locationRef,
                tenancyId
              },
              true
            )
        ]);
      }
    );

  openInvoiceDetailModal = invoiceId =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS
      },
      () => {
        const {scheduleType, locationRef, tenancyId} = this.props.data;

        this.props.composeActions([
          () => closeModal(MODALS.CREDIT_NOTE_DETAIL),
          () =>
            openModal(
              MODALS.INVOICE_DETAIL,
              {
                scheduleType,
                invoiceId,
                locationRef,
                tenancyId
              },
              true
            )
        ]);
      }
    );

  render() {
    const creditNote = _.get(this.props, 'data.creditNote');
    const scheduleType = _.get(this.props, 'data.scheduleType');
    const errors = _.get(this.props, 'data.errors', {});
    const locationRef = _.get(this.props, 'data.locationRef');
    const tenancyId = _.get(this.props, 'data.tenancyId');
    const canBeDeallocated = _.isNil(
      _.get(this.props, 'data.creditNote.invoices[0].id')
    );

    const xeroSyncErrors = _.get(this.props, 'data.creditNote.xeroErrors', []);

    return (
      <div>
        {creditNote && (
          <Modal
            show={true}
            onClose={this.props.closeModal}
            className="CreditNoteDetailModal"
            type={Modal.LARGE}>
            <Modal.Header>
              <React.Fragment>
                <div>
                  <h3 className="header">Credit note {creditNote.idString}</h3>
                  <CreditNoteStatusTag
                    status={creditNote.status}
                    label={creditNote.status}
                  />
                </div>
              </React.Fragment>
            </Modal.Header>
            <Modal.Body>
              <Modal.Errors errors={errors} />
              <Modal.Errors errors={xeroSyncErrors} />

              <BaseInformation
                creditNote={creditNote}
                voidCreditNote={this.voidCreditNote}
                addXeroIdToCreditNote={this.addXeroIdToCreditNote}
                syncCreditNoteWithXero={this.syncCreditNoteWithXero}
                deallocateCreditNoteFromInvoice={
                  this.deallocateCreditNoteFromInvoice
                }
                canBeDeallocated={canBeDeallocated}
              />
              <CreditNoteItemsTable creditNote={creditNote} />
              <Totals creditNote={creditNote} />
              <LessSection
                payments={creditNote.payments}
                invoices={creditNote.invoices}
                openRefundDetailModal={this.openRefundDetailModal}
                openInvoiceDetailModal={this.openInvoiceDetailModal}
              />
              <RemainingCredit remainingCredit={creditNote.remainingCredit} />
              {creditNote.remainingCredit !== 0.0 && (
                <VisibleIf
                  permission={PERMISSIONS.ADD_REFUND}
                  locationRef={locationRef}>
                  <ReceiveCashRefund
                    delayExtraActions={this.props.delayExtraActions}
                    tenancyId={tenancyId}
                    creditNoteId={creditNote.id}
                    getExtraActions={this.getSuccessExtraActions}
                    scheduleType={scheduleType}
                    storeDataExtraActions={this.props.storeDataExtraActions}
                    locationRef={locationRef}
                  />
                </VisibleIf>
              )}
            </Modal.Body>
          </Modal>
        )}
      </div>
    );
  }
}

const mapStateToModalData = (_state, data) => {
  return {
    warnings: _.get(data, 'invoiceAllocationWarnings')
  };
};

const mapDispatchToProps = {
  fetchCreditNote,
  voidObject,
  addXeroId,
  syncWithXero,
  fetchXeroAccounts,
  requiresPermission,
  delayedAction,
  openModal,
  deallocateCreditNoteFromInvoice,
  fetchInvoiceAllocatedCreditNotesXeroLinks,
  composeActions
};

export default withModal(
  MODALS.CREDIT_NOTE_DETAIL,
  mapStateToModalData,
  mapDispatchToProps
)(CreditNoteDetailModal);
